<template>
  <div class="wt-transcribe-player -keep-token-editor-open">
    <div :class="['-play-pause', { '-disabled': !canPlay }]">
      <span
        :class="[
          'wticon',
          {
            'wticon-play1': !isPlaying,
            'wticon-pause': isPlaying,
          },
        ]"
        @click="playPause"
      />
    </div>
    <wt-slider
      class="-slider"
      v-model="sliderPoint"
      :min="0"
      :max="sliderPoints"
      :showStepsMarker="false"
      :showInputBox="false"
      @mousemove.native="onMouseMove"
      @mouseup.native="onMouseUp"
      @mousedown.native="onMouseDown"
      :disabled="!canPlay"
    />
    <div class="-duration wt2-text-n" v-text="durationDisplay" />
  </div>
</template>

<script>
export default {
  name: 'wt-transcribe-player',
  props: {
    src: {
      type: String,
      required: true,
    },
    timeLimit: {
      type: [Object, Boolean],
      required: false,
      default: false,
    },
  },
  data() {
    return {
      sliderPoints: 1000,
      sliderPoint: 0,
      isPlaying: false,
      player: null,
      playInterval: null,
      isManualSeeking: null,
      shouldPlayOnSeekEnd: false,
      canPlay: false,
      availableDuration: 0,
      startTimeFix: 0,
      onCanPlayTime: null,
    };
  },
  mounted() {
    this.player = document.createElement('audio');
    this.player.src = this.src;
    this.player.addEventListener('play', () => {
      this.isPlaying = true;
      this.playInterval = window.setInterval(() => {
        const timePercent =
          (this.player.currentTime - this.startTimeFix) /
          this.availableDuration;
        this.sliderPoint = timePercent * this.sliderPoints;
        this.emitTime();
        if (timePercent < 0 || timePercent >= 1) {
          this.player.pause();
          return;
        }
      }, 25);
    });
    this.player.addEventListener('pause', () => {
      this.isPlaying = false;
      window.clearInterval(this.playInterval);
    });
    this.player.addEventListener('loadedmetadata', () => {
      if (this.timeLimit) {
        // check if timeLimit is valid within asset duration
        if (this.timeLimit.startTime > this.player.duration) {
          this.timeLimit.startTime = 0;
        }
        if (this.timeLimit.endTime > this.player.duration) {
          this.timeLimit.endTime = this.player.duration;
        }
        // set startTime according to limitations
        this.player.currentTime = this.timeLimit.startTime;
      }

      // set duration according to limited timings
      this.availableDuration = this.timeLimit
        ? this.timeLimit.endTime - this.timeLimit.startTime
        : this.player.duration;

      // startTime margin according to timelimit
      this.startTimeFix = this.timeLimit ? this.timeLimit.startTime : 0;

      this.canPlay = true;

      this.$emit('canPlay');

      if (this.onCanPlayTime) {
        this.changeTime(this.onCanPlayTime, { isManual: true });
        this.onCanPlayTime = null;
      }
    });
    app.keyboardAccessibility.add('wt-transcribe-player', [
      {
        on: 'keydown',
        key: $.ui.keyCode.SPACE,
        run: this.onKeySpace,
      },
    ]);
  },
  beforeDestroy() {
    window.clearInterval(this.playInterval);
    this.player.pause();
    this.player.src = null;
    this.player = null;
    app.keyboardAccessibility.remove('wt-transcribe-player');
  },
  methods: {
    onKeySpace(e) {
      e.preventDefault();
      this.playPause();
    },
    playPause() {
      if (this.isPlaying) {
        this.pause();
      } else {
        this.play();
      }
    },
    pause() {
      this.player.pause();
    },
    play() {
      if (this.timeLimit && this.player.currentTime >= this.timeLimit.endTime) {
        this.player.currentTime = this.timeLimit.startTime;
      }
      this.$_uncaughtSuppressor(this.player.play());
    },
    changeTime(time, options = { isManual: false }) {
      if (!this.canPlay) {
        this.onCanPlayTime = time;
        return;
      }
      time = this.fixTimeByLimit(time);
      this.player.currentTime = time;
      this.emitTime();
      if (options.isManual) {
        const timePercent = (time - this.startTimeFix) / this.availableDuration;
        this.sliderPoint = timePercent * this.sliderPoints;
      }
    },
    fixTimeByLimit(time) {
      if (this.timeLimit) {
        if (time < this.timeLimit.startTime) {
          time = this.timeLimit.startTime;
        } else if (time > this.timeLimit.endTime) {
          time = this.timeLimit.endTime;
        }
      }
      return time;
    },
    emitTime() {
      this.$emit('timeChanged', app.sfp(this.player.currentTime, 1e3));
    },
    onMouseDown() {
      this.shouldPlayOnSeekEnd = this.isPlaying;
      window.clearInterval(this.playInterval);
      this.player.pause();
      this.isManualSeeking = true;
    },
    onMouseUp() {
      this.isManualSeeking = false;
      if (document.body.classList.contains('-no-select')) {
        // text highlighting in progress
        return;
      }
      const newPercent = this.sliderPoint / this.sliderPoints;
      this.changeTime(this.startTimeFix + this.availableDuration * newPercent);
      if (this.shouldPlayOnSeekEnd) {
        this.$_uncaughtSuppressor(this.player.play());
      }
    },
    onMouseMove() {
      if (document.body.classList.contains('-no-select')) {
        // text highlighting in progress
        return;
      }
      if (this.isManualSeeking) {
        const newPercent = this.sliderPoint / this.sliderPoints;
        this.changeTime(
          this.startTimeFix + this.availableDuration * newPercent
        );
      }
    },

    $_uncaughtSuppressor(playResponse) {
      if (playResponse && _.isFunction(playResponse.catch)) {
        playResponse.catch(_.noop);
      }
    },
  },
  computed: {
    durationDisplay() {
      return app.moment.utc(this.availableDuration * 1000).format('m:ss');
    },
  },
};
</script>

<style lang="scss" scoped>
.wt-transcribe-player {
  display: flex;
  flex-direction: row;
  align-items: center;

  > div {
    display: flex;
    align-items: center;
  }
  .-play-pause {
    margin: 0 15px 0 0;
    .wticon {
      font-size: 14px;
      cursor: pointer;
    }
    &.-disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
  .-slider {
    width: 100%;
    flex-grow: 1;
    padding: 0;
    /deep/ .-seek-box {
      margin-top: 0;
    }
    /deep/ .-fake-range {
      height: 2px;
    }
    /deep/ input[type='range']:disabled {
      &::-webkit-slider-runnable-track {
        height: 2px;
      }
    }
  }
  .-duration {
    margin-left: 15px;
  }
}
</style>
